<template>
    <div>
        <div v-if="authenticated">
            <b-row class="mb-4">
                <b-col
                    cols="12"
                    lg="8"
                >
                    <!-- Heading -->
                    <h1 class="mb-3">
                        My Dashboard
                    </h1>
                    <p class="text-bold color-dark-grey">
                        Today, {{ new Date() | moment('MMM Do YYYY') }}&nbsp;
                        <router-link :to="{ name: 'notifications' }">
                            {{ notificationCounts.alerts }} new {{ 'notification' + (notificationCounts.alerts !== 1 ? 's' : '') }}
                        </router-link>,
                        <router-link :to="{ name: 'messages' }">
                            {{ notificationCounts.messages }} new {{ 'message' + (notificationCounts.messages !== 1 ? 's' : '') }}
                        </router-link>
                    </p>

                    <p class="text-demibold">
                        Welcome to your My Online Estate Agent dashboard. From here, you can view all messages, notifications and calendar appointments for your properties. You can also use the navigation on the left side of the page to search for, save and add your own properties.
                    </p>
                </b-col>
                <b-col
                    cols="12"
                    lg="4"
                    class="d-flex flex-column flex-xl-row justify-content-xl-end"
                >
                    <!-- Property buttons -->
                    <div class="mb-3 mt-4 mt-xl-0 mr-xl-3">
                        <router-link
                            :to="{ name: 'checkout' }"
                            class="btn btn--primary"
                        >
                            Add new Property
                        </router-link>
                    </div>
                    <div>
                        <router-link
                            :to="{ name: 'my-properties' }"
                            class="btn btn--secondary"
                        >
                            My Properties
                        </router-link>
                    </div>
                    <!-- END Property buttons -->
                </b-col>
            </b-row>

            <b-row>
                <b-col
                    cols="12"
                    xl="6"
                    class="mb-5"
                >
                    <!-- Messages -->
                    <div class="card h-100">
                        <div class="card__body d-flex flex-column">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h2 class="card__title mb-3">
                                        My Messages
                                    </h2>
                                    <p>Here are your most recent unread messages</p>
                                </div>
                                <SVGSprite
                                    name="messages"
                                    class="card-icon"
                                />
                            </div>

                            <div
                                v-if="threads && threads.data.length"
                                class="mt-5"
                            >
                                <div
                                    v-for="thread in threads.data.slice(0, messagesLimit)"
                                    :key="thread.id"
                                >
                                    <!-- Message -->
                                    <b-row class="align-items-center dashboard-message">
                                        <b-col
                                            cols="3"
                                            md="2"
                                            class="text-md-center mb-4 mb-md-0"
                                        >
                                            <p class="h1 color-primary mb-0">
                                                {{ thread.updated_at | moment('DD') }}
                                            </p>
                                            <p class="h5 color-primary mb-0 text-bold">
                                                {{ thread.updated_at | moment('MMM') }}
                                            </p>
                                        </b-col>
                                        <b-col
                                            cols="8"
                                            md="7"
                                        >
                                            <p class="color-dark-grey text-bold">
                                                {{ thread.creator.last_name }}, {{ thread.creator.first_name }}
                                            </p>
                                            <p class="color-mid-grey mb-0">
                                                {{ thread.subject }}
                                            </p>
                                        </b-col>
                                        <b-col
                                            cols="1"
                                            md="3"
                                            class="text-md-right mt-4 mt-md-0 d-none d-lg-inline-block"
                                        >
                                            <router-link
                                                :to="{ name: 'message', params: { threadId: thread.id } }"
                                                class="btn btn--outline btn--nomin btn--sm"
                                            >
                                                View
                                            </router-link>
                                        </b-col>
                                    </b-row>
                                    <!-- END Message -->
                                </div>
                            </div>

                            <!-- View all messages -->
                            <div class="d-flex align-items-center justify-content-start justify-content-lg-end mt-auto">
                                <router-link
                                    :to="{ name: 'messages' }"
                                    class="btn btn--secondary"
                                >
                                    My Messages
                                </router-link>
                            </div>
                        </div>
                    </div>
                    <!-- END Messages -->
                </b-col>
                <b-col
                    cols="12"
                    xl="6"
                    class="mb-5"
                >
                    <!-- Alerts globally -->
                    <div class="card h-100">
                        <div class="card__body d-flex flex-column">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h2 class="card__title mb-3">
                                        My Notifications
                                    </h2>
                                    <p>Here are your most recent account notifications</p>
                                </div>
                                <SVGSprite
                                    name="bell"
                                    class="card-icon"
                                />
                            </div>

                            <!-- Alerts -->
                            <div
                                v-if="user.notifications"
                                class="mt-5"
                            >
                                <!-- Alert item -->
                                <b-row
                                    v-for="notification in notifications"
                                    :key="notification.id"
                                    class="align-items-center dashboard-alert"
                                >
                                    <b-col
                                        cols="12"
                                        lg="3"
                                        xl="2"
                                    >
                                        <span
                                            :class="{
                                                'label-type--sell': notification.property_type === 'sale',
                                                'label-type--let': notification.property_type === 'rent'
                                            }"
                                            class="label-type mb-4 mb-lg-0"
                                        >
                                            {{ notification.label }}
                                        </span>
                                    </b-col>
                                    <b-col
                                        cols="11"
                                        lg="6"
                                        xl="7"
                                    >
                                        <p class="color-dark-grey text-bold mb-0">
                                            {{ notification.content }}
                                        </p>
                                    </b-col>
                                    <b-col
                                        cols="12"
                                        lg="3"
                                        class="d-flex justify-content-end align-items-center mt-4 mt-lg-0"
                                    >
                                        <button
                                            class="btn btn--outline btn--nomin btn--sm d-inline-block"
                                            type="button"
                                            @click="viewRelatedLink(notification)"
                                        >
                                            View
                                        </button>
                                        <button
                                            v-if="!notification.read_at"
                                            type="button"
                                            class="remove-button"
                                            @click="markNotificationAsRead(notification)"
                                        >
                                            <SVGSprite
                                                name="close"
                                                class="remove-icon"
                                            />
                                        </button>
                                    </b-col>
                                </b-row>
                            </div>

                            <!-- View all alerts -->
                            <div class="d-flex align-items-center justify-content-start justify-content-lg-end mt-auto">
                                <router-link
                                    :to="{ name: 'notifications' }"
                                    class="btn btn--secondary"
                                >
                                    My Notifications
                                </router-link>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row>
                <b-col
                    cols="12"
                    xl="6"
                    class="mb-5"
                >
                    <!-- Calendar -->
                    <div class="card h-100">
                        <div class="card__body d-flex flex-column">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h2 class="card__title mb-3">
                                        My Calendar
                                    </h2>
                                    <p v-if="sortedEvents.approved && sortedEvents.approved.length">
                                        Here are your upcoming appointments
                                    </p>
                                    <p v-else>
                                        You do not have any upcoming appointments
                                    </p>
                                </div>
                                <SVGSprite
                                    name="calendar"
                                    class="card-icon"
                                />
                            </div>

                            <!-- Alerts for appointments -->
                            <div
                                v-if="sortedEvents.approved && sortedEvents.approved.length"
                                class="mt-5"
                            >
                                <!-- Alert item -->
                                <b-row
                                    v-for="event in sortedEvents.approved.slice(0, 5)"
                                    :key="event.id"
                                    class="align-items-md-center dashboard-alert"
                                >
                                    <b-col
                                        cols="12"
                                        lg="2"
                                    >
                                        <span class="label-type label-type--sell">{{ event.label }}</span>
                                    </b-col>
                                    <b-col
                                        cols="12"
                                        lg="2"
                                        xl="3"
                                        class="text-lg-center mt-4 mt-lg-0"
                                    >
                                        <p class="mb-0 p-sm color-dark-grey text-bold">
                                            {{ event.start_at | moment('L') }}
                                        </p>
                                    </b-col>
                                    <b-col
                                        cols="12"
                                        lg="2"
                                        class="text-lg-center mt-2 mt-lg-0"
                                    >
                                        <p class="mb-0 p-sm color-dark-grey text-bold">
                                            {{ event.start_at | moment('h:mm a') }}
                                        </p>
                                    </b-col>
                                    <b-col
                                        cols="11"
                                        lg="6"
                                        xl="5"
                                        class="mt-4 mt-lg-0"
                                    >
                                        <p class="mb-0 p-sm">
                                            {{ event.property_name }}
                                        </p>
                                    </b-col>
                                </b-row>
                                <!-- END Alert item -->
                            </div>

                            <!-- View all diary -->
                            <div class="d-flex align-items-center justify-content-start justify-content-lg-end mt-auto">
                                <router-link
                                    :to="{ name: 'my-calendar' }"
                                    class="btn btn--secondary"
                                >
                                    My Calendar
                                </router-link>
                            </div>
                        </div>
                    </div>
                </b-col>
                <b-col
                    cols="12"
                    xl="6"
                    class="mb-5"
                >
                    <!-- Other? -->
                    <div class="card h-100">
                        <div class="card__body">
                            <b-carousel
                                v-model="sliderDash"
                                :interval="4000"
                                class="dashboard-slider"
                                controls
                                indicators
                                @sliding-start="onSlideDashStart"
                                @sliding-end="onSlideDashEnd"
                            >
                                <b-carousel-slide
                                    v-for="(slide, index) in slidesDash"
                                    :key="index"
                                    :img-src="slide.url"
                                />
                            </b-carousel>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-modal
                v-if="slides && slides.length > 0"
                id="modal-welcome"
                ref="modalWelcome"
                class="modal-welcome"
                size="lg"
                hide-header
                hide-footer
                no-close-on-backdrop
                no-close-on-esc
            >
                <b-carousel
                    v-model="slider"
                    :interval="4000"
                    class="modal-welcome__slider"
                    controls
                    indicators
                    @sliding-start="onSlideStart"
                    @sliding-end="onSlideEnd"
                >
                    <b-carousel-slide
                        v-for="(slide, index) in slides"
                        :key="index"
                        :img-src="slide.url"
                    />
                    <button
                        class="modal-welcome__hide btn btn--secondary btn--nomin"
                        @click="hideWelcome()"
                    >
                        Start
                    </button>
                </b-carousel>
            </b-modal>
        </div>
        <div v-else>
            <p>Unauthenticated</p>
            <router-link to="/">
                Login
            </router-link>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import common from 'js/mixins/common';

export default {
    name: 'Dashboard',
    mixins: [
        common
    ],
    data() {
        return {
            messagesLimit: 3,
            alertsLimit: 4,
            propertiesLimit: 4,
            slider: 0,
            sliding: null,
            slides: [
                {
                    url: '/images/welcome-1.png'
                },
                {
                    url: '/images/welcome-2.png'
                },
                {
                    url: '/images/welcome-3.png'
                },
                {
                    url: '/images/welcome-4.png'
                },
                {
                    url: '/images/welcome-5.png'
                },
                {
                    url: '/images/welcome-6.png'
                }
            ],
            sliderDash: 0,
            slidingDash: null,
            slidesDash: [
                {
                    url: '/images/dash-ban-mortgage.png'
                },
                {
                    url: '/images/dash-ban-conveyancing.png'
                }
            ],
            removedNotifications: []
        };
    },
    computed: {
        ...mapState('messages', ['threads']),
        ...mapState('user', ['user']),
        ...mapGetters('user', ['notificationCounts', 'sortedEvents']),
        ...mapGetters('notifications', ['getRelatedLink']),
        notifications() {
            try {
                return this.user.notifications.filter(notification => !notification.read_at && this.removedNotifications.indexOf(notification.id) === -1).slice(0, this.alertsLimit);
            }
            catch (e) {
                return [];
            }
        }
    },
    mounted() {
        this.getThreads(this.$route.params.page || 1);

        // Dont show the 'wecome only' slides again
        if (localStorage.welcome_seen !== 'true') {
            this.$refs.modalWelcome.show();
        }
    },
    methods: {
        ...mapActions('messages', ['getThreads']),
        ...mapActions('notifications', {
            markNotificationsAsRead: 'markAsRead'
        }),
        hideWelcome() {
            localStorage.welcome_seen = true;
            this.$refs.modalWelcome.hide();
            this.$router.push({ name: 'my-profile' });
        },
        onSlideStart(slide) {
            this.sliding = true;
        },
        onSlideEnd(slide) {
            this.sliding = false;
        },
        onSlideDashStart(slide) {
            this.slidingDash = true;
        },
        onSlideDashEnd(slide) {
            this.slidingDash = false;
        },
        markNotificationAsRead(notification) {
            notification.read_at = true;
            this.markNotificationsAsRead({ notificationIds: [notification.id] });
        },
        viewRelatedLink(notification) {
            if (!notification.read_at) {
                this.markNotificationAsRead(notification);
            }
            this.$router.push(this.getRelatedLink(notification));
        }
    }
};
</script>

<style lang="scss">
// Add property custom button
.add-property {
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;

    @include media-breakpoint-up(lg) {
        min-width: 20rem;
    }

    .btn-icon {
        width: 3rem;
        margin-left: 4rem;
    }
}

// Title icons
.card-icon {
    color: theme-color('secondary');
    width: 3rem;
}

// Custom list styling
.dashboard-message, .dashboard-alert {
    border-bottom: 1px solid theme-color('light-grey');
    padding: 1.5rem 0;

    @include media-breakpoint-up(md) {
        padding: 1rem 0;
    }

    &:last-of-type {
        border-bottom: 0;
    }
}

// Custom message style for dashboard
.dashboard-message {
    @include media-breakpoint-down(sm) {
        position: relative;

        &::after {
            content: "";
            width: 12px;
            height: 12px;
            background: transparent;
            border-left: 1px solid theme-color('mid-grey');
            border-bottom: 1px solid theme-color('mid-grey');
            position: absolute;
            right: 26px;
            top: 27%;
            transform: translateY(-50%) rotate(-135deg);
        }
    }
}

// welcome modal
.modal {
    &#modal-welcome {
        .modal-backdrop.show {
            opacity: 0.7;
        }

        .modal-dialog {
            margin: 0 auto;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            .modal-content {
                padding: 5%;
                background: transparent;
                border-radius: 0;
                height: 100%;

                .modal-body {
                    padding: 0;
                    height: 100%;
                }
            }
        }

        .modal-welcome__slider {
            // height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                width: auto !important;
                max-height: 100%;
                margin: 0 auto;
            }

            .carousel-inner {
                height: 100%;
            }

            .carousel-item {
                height: 100%;
            }

            .carousel-indicators {
                margin: 0 0 7.5px 0;
                position: fixed;

                li {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;

                    &:focus {
                        outline: none;
                    }
                }
            }
        }

        .modal-welcome__hide {
            position: absolute;
            bottom: 0;
            right: 0;
            margin: 1.5rem;
            z-index: 10;
            width: auto;
        }
    }
}
</style>
